import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import analyzeExcelHeadersTool from 'common/src/modules/shared/copilot/tools/analyzeExcelHeadersTool/analyzeExcelHeadersTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {DistributionCardScratchpadState} from '../../scratchpad/distributionCardScratchpad.types';
import getOrCreateDistributionCard from '../../utils/distribution/getOrCreateDistributionCard';
import addDistributionOutputTool from './addDistributionOutputTool';
import editDistributionOutputTool from './editDistributionOutputTool';
import deleteDistributionOutputsTool from './deleteDistributionOutputsTool';

const cardSpecificInstructions = `### What is a Distribution card
A Distribution card is used in social campaigns only. It routes traffic across outputs, and the social network (e.g., Facebook, TikTok) decides how much traffic to allocate to each active ad path (automatic optimization). Outputs have no weights.

### Distribution card rules
- Distribution cards do NOT have fallback outputs.
- Must have at least 2 outputs at all times.

### Property Dependencies
- All properties (outputs, card name, description) are independent — none depend on another being set first.`;

export default function addOrUpdateDistributionCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, DistributionCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateDistributionCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateDistributionCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.DISTRIBUTION]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.DISTRIBUTION,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getFilesTool(),
        getCardDetailsTool(),
        analyzeExcelHeadersTool(),
        readValuesFromExcelTool(),

        addDistributionOutputTool(),
        editDistributionOutputTool(),
        deleteDistributionOutputsTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateDistributionCard,
    description:
      'Add or update a Distribution card. Routes traffic across outputs based on campaign optimization logic. Supports reading values from Excel.',
    displayName: 'Working on distribution card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
